import React, { useEffect, useRef } from 'react';
import { createChart, ColorType, IChartApi, ISeriesApi } from 'lightweight-charts';
import { useCandles, useIndicators } from '../hooks/useMarketData';
import type { TimeRange, IndicatorType, IndicatorPeriod } from '../types';

interface Props {
  range: TimeRange;
  indicatorType: IndicatorType;
  indicatorPeriod: IndicatorPeriod;
}

export const LineChart: React.FC<Props> = ({ range, indicatorType, indicatorPeriod }) => {
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const priceSeriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  const indicatorSeriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  
  const { data: candleData, isLoading: candlesLoading } = useCandles(range);
  const { data: indicatorData, isLoading: indicatorsLoading } = useIndicators(indicatorType, indicatorPeriod, range);

  const isLoading = candlesLoading || indicatorsLoading;

  // Create chart once when we have data and container is ready
  useEffect(() => {
    if (!chartContainerRef.current || !candleData || chartRef.current) return;

    // Wait for container to have proper width
    const containerWidth = chartContainerRef.current.clientWidth;
    if (containerWidth === 0) return;

    const chart = createChart(chartContainerRef.current, {
      layout: {
        background: { type: ColorType.Solid, color: 'white' },
        textColor: '#333',
      },
      width: containerWidth,
      height: 300,
      timeScale: {
        timeVisible: true,
        secondsVisible: false,
      },
      grid: {
        vertLines: { color: '#f0f0f0' },
        horzLines: { color: '#f0f0f0' },
      },
    });

    const priceSeries = chart.addLineSeries({
      color: '#2563eb',
      lineWidth: 2,
      title: 'Price',
    });

    const indicatorSeries = chart.addLineSeries({
      color: '#f59e0b',
      lineWidth: 2,
      title: `${indicatorType.toUpperCase()}-${indicatorPeriod}`,
    });

    chartRef.current = chart;
    priceSeriesRef.current = priceSeries;
    indicatorSeriesRef.current = indicatorSeries;

    // Set initial data immediately
    if (candleData) {
      const priceData = candleData.candles.map((candle) => ({
        time: Math.floor(new Date(candle.timestamp).getTime() / 1000) as any,
        value: candle.close,
      }));
      priceSeries.setData(priceData as any);
      chart.timeScale().fitContent();
    }

    if (indicatorData) {
      const indicatorPoints = indicatorData.data
        .filter((point) => point.indicator !== null)
        .map((point) => ({
          time: Math.floor(new Date(point.timestamp).getTime() / 1000) as any,
          value: point.indicator,
        }));
      indicatorSeries.setData(indicatorPoints as any);
    }

    const handleResize = () => {
      if (chartContainerRef.current && chartRef.current) {
        chart.applyOptions({
          width: chartContainerRef.current.clientWidth,
        });
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (chartRef.current) {
        chartRef.current.remove();
        chartRef.current = null;
      }
      priceSeriesRef.current = null;
      indicatorSeriesRef.current = null;
    };
  }, [candleData, indicatorType, indicatorPeriod]);

  // Update price data when candles change
  useEffect(() => {
    if (!candleData || !priceSeriesRef.current || !chartRef.current) return;

    const priceData = candleData.candles.map((candle) => ({
      time: Math.floor(new Date(candle.timestamp).getTime() / 1000) as any,
      value: candle.close,
    }));

    priceSeriesRef.current.setData(priceData as any);
    chartRef.current.timeScale().fitContent();
  }, [candleData]);

  // Update indicator data when indicators change
  useEffect(() => {
    if (!indicatorData || !indicatorSeriesRef.current || !chartRef.current) return;

    const indicatorPoints = indicatorData.data
      .filter((point) => point.indicator !== null)
      .map((point) => ({
        time: Math.floor(new Date(point.timestamp).getTime() / 1000) as any,
        value: point.indicator,
      }));

    indicatorSeriesRef.current.setData(indicatorPoints as any);
  }, [indicatorData]);

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-800">
          Price with {indicatorType.toUpperCase()}-{indicatorPeriod}
        </h3>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-blue-600 rounded-full"></div>
            <span className="text-sm text-gray-600">Price</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-amber-500 rounded-full"></div>
            <span className="text-sm text-gray-600">
              {indicatorType.toUpperCase()}-{indicatorPeriod}
            </span>
          </div>
        </div>
      </div>

      <div className="relative">
        {isLoading && (
          <div className="absolute inset-0 h-[300px] flex items-center justify-center bg-gray-50 rounded z-10">
            <div className="text-gray-500">Loading chart...</div>
          </div>
        )}
        <div ref={chartContainerRef} className="h-[300px] w-full" />
      </div>
    </div>
  );
};
